import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { nextCursor, SafewardClient, SafewardHttpError } from '../safeward-client.js'
import type {
  AppSettings,
  ZerionTransaction,
  ZerionTransactionsPage,
} from '../types.js'

export interface TxFilters {
  /** Comma-separated Zerion chain slugs (`ethereum,base,arbitrum`). */
  chainIds?: string
  /** Comma-separated Zerion operation_types (`send,receive,trade`). */
  operationTypes?: string
  pageSize?: number
  trash?: 'only_non_trash' | 'only_trash' | 'no_filter'
}

export type TransactionsState =
  | { status: 'idle' }
  | { status: 'loading' }
  | {
      status: 'ready'
      txs: ZerionTransaction[]
      cursor: string | null
      loadingMore: boolean
      /** Transient pagination error — set when loadMore fails but we have
       *  prior pages we don't want to discard. */
      loadMoreError: string | null
      refreshedAt: Date
    }
  | { status: 'error'; message: string; isUnavailable: boolean }

export interface UseTransactions {
  state: TransactionsState
  refresh: () => Promise<void>
  loadMore: () => Promise<void>
}

const DEFAULT_PAGE_SIZE = 25

/**
 * Paginated tx history for an address. Holds an accumulating array of txs
 * and the next cursor; `loadMore()` appends the next page. Resets on
 * address or filter change.
 */
export function useTransactions(
  settings: AppSettings,
  address: string | null,
  filters: TxFilters = {},
): UseTransactions {
  const [state, setState] = useState<TransactionsState>({ status: 'idle' })

  const client = useMemo(
    () =>
      new SafewardClient({
        baseUrl: settings.safewardUrl,
        authToken: settings.safewardAuthToken,
      }),
    [settings.safewardUrl, settings.safewardAuthToken],
  )
  const clientRef = useRef(client)
  clientRef.current = client

  const filtersKey = JSON.stringify({
    chainIds: filters.chainIds ?? '',
    operationTypes: filters.operationTypes ?? '',
    pageSize: filters.pageSize ?? DEFAULT_PAGE_SIZE,
    trash: filters.trash ?? 'only_non_trash',
  })
  const filtersRef = useRef(filters)
  filtersRef.current = filters
  const addressRef = useRef(address)
  addressRef.current = address

  const fetchPage = useCallback(
    async (cursor: string | null): Promise<ZerionTransactionsPage> => {
      const addr = addressRef.current
      if (!addr) throw new Error('no address')
      const f = filtersRef.current
      return clientRef.current.transactions(addr, {
        chainIds: f.chainIds,
        operationTypes: f.operationTypes,
        pageSize: f.pageSize ?? DEFAULT_PAGE_SIZE,
        trash: f.trash ?? 'only_non_trash',
        sort: '-mined_at',
        pageAfter: cursor ?? undefined,
      })
    },
    [],
  )

  const refresh = useCallback(async () => {
    const addr = addressRef.current
    if (!addr) {
      setState({ status: 'idle' })
      return
    }
    setState({ status: 'loading' })
    try {
      const page = await fetchPage(null)
      setState({
        status: 'ready',
        txs: page.data,
        cursor: nextCursor(page),
        loadingMore: false,
        loadMoreError: null,
        refreshedAt: new Date(),
      })
    } catch (err) {
      const isUnavailable =
        err instanceof SafewardHttpError && err.status === 503
      setState({
        status: 'error',
        message: (err as Error).message,
        isUnavailable,
      })
    }
  }, [fetchPage])

  const loadMore = useCallback(async () => {
    // Race-free cursor read: capture the cursor INSIDE the setState updater
    // so we observe the same snapshot that's flipping `loadingMore`. If
    // we're not in `ready`, have no cursor, or are already loading, bail
    // without committing any state change.
    let cursor: string | null = null
    let proceed = false
    setState((prev) => {
      if (prev.status !== 'ready' || !prev.cursor || prev.loadingMore) return prev
      cursor = prev.cursor
      proceed = true
      return { ...prev, loadingMore: true, loadMoreError: null }
    })
    if (!proceed) return

    try {
      const page = await fetchPage(cursor)
      setState((prev) => {
        if (prev.status !== 'ready') return prev
        return {
          ...prev,
          txs: [...prev.txs, ...page.data],
          cursor: nextCursor(page),
          loadingMore: false,
        }
      })
    } catch (err) {
      // Transient pagination failure — preserve the already-loaded txs and
      // surface the error inline rather than wiping the list to a global
      // 'error' state.
      const message = (err as Error).message
      setState((prev) => {
        if (prev.status !== 'ready') return prev
        return { ...prev, loadingMore: false, loadMoreError: message }
      })
    }
  }, [fetchPage])

  // Reset + refetch on address or filters change.
  useEffect(() => {
    void refresh()
  }, [refresh, address, filtersKey])

  return { state, refresh, loadMore }
}
